import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import {
  faCheck,
  faCircleInfo,
  faCog,
  faPaperPlane,
  faPause,
  faRotate,
  faStop,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { PANEL_EVENT_TIME_ZONE } from "../../assets/functions.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import useAppDispatch from "../../hooks/useAppDispatch.js";
import useAuth from "../../hooks/useAuth.js";
import useLayout from "../../hooks/useLayout.js";
import { eventList } from "../../redux/slices/events.js";
import { canAccessEventRsvps, canAccessEventWhatsApp } from "../../utils/eventPermissions.js";
import { getWhatsAppRsvpCount } from "../../utils/whatsappRsvpSummary.js";
import { formatWhatsAppTemplateVersion } from "../../utils/whatsappTemplateVersion.js";
import { getWhatsAppTemplateBlockMessages, isDeletedWhatsAppTemplate } from "../../utils/whatsappTemplatePresentation.js";
import {
  cancelWhatsAppCampaign,
  createWhatsAppCampaign,
  getWhatsAppDelivery,
  getWhatsAppCampaign,
  getWhatsAppParticipants,
  getWhatsAppSenders,
  getWhatsAppSettings,
  previewWhatsAppResend,
  resendWhatsAppDeliveries,
  resolveWhatsAppDelivery,
  getWhatsAppTemplates,
  isWhatsAppRequestCancelled,
  pauseWhatsAppCampaign,
  previewWhatsAppCampaign,
  putWhatsAppSettings,
  resumeWhatsAppCampaign,
} from "../../api/whatsapp.js";
import { getEventRsvpSettings } from "../../api/rsvp.js";
import EventRsvpManagerModal from "../../components/EventRsvpManagerModal.js";
import Loader from "../../components/Loader.js";
import WhatsAppRegistrationHistoryCard from "../../components/WhatsAppRegistrationHistoryCard.js";
import WhatsAppCampaignReport from "../../components/WhatsAppCampaignReport.js";
import WhatsAppBlocklist from "../../components/WhatsAppBlocklist.js";
import { loadWhatsAppCampaignReport } from "../../utils/whatsappReports.js";

const CAMPAIGN_STATUS = {
  draft: { label: "Rascunho", bg: "secondary" },
  scheduled: { label: "Agendada", bg: "primary" },
  running: { label: "Em envio", bg: "warning" },
  paused: { label: "Pausada", bg: "warning" },
  completed: { label: "Concluída", bg: "success" },
  cancelled: { label: "Cancelada", bg: "dark" },
  failed: { label: "Falhou", bg: "danger" },
};

const DELIVERY_STATUS = {
  pending: { label: "Pendente", bg: "secondary" },
  reserved: { label: "Reservado", bg: "secondary" },
  submitting: { label: "Em submissão", bg: "warning" },
  accepted: { label: "Aceite pela Meta", bg: "primary" },
  delivered: { label: "Entregue", bg: "success" },
  read: { label: "Lido", bg: "success" },
  failed: { label: "Falhou", bg: "danger" },
  unknown: { label: "Resultado incerto", bg: "warning" },
  suppressed: { label: "Suprimido", bg: "dark" },
  cancelled: { label: "Cancelado", bg: "dark" },
};

const RSVP_FEEDBACK_STATUS = {
  ...DELIVERY_STATUS,
  sending: { label: "Enviando retorno", bg: "warning" },
  sent: { label: "Aceite pela Meta", bg: "primary" },
  expired: { label: "Janela de resposta encerrada", bg: "dark" },
  superseded: { label: "Substituído por resposta mais recente", bg: "secondary" },
};

const AUDIENCE_EXCLUSION_LABELS = {
  phone_invalid: "telefone inválido ou ausente",
  phone_duplicate: "telefone compartilhado/duplicado",
  suppressed: "número suprimido",
  cooldown: "em intervalo obrigatório entre mensagens",
  rate_limited: "em intervalo obrigatório entre mensagens",
  rsvp_already_answered: "RSVP já respondido",
  template_variable_missing: "dados do modelo ausentes ou imagem indisponível",
};

const CAMPAIGN_PURPOSE_LABELS = {
  rsvp: "RSVP",
  event_update: "Atualização do evento",
  marketing: "Marketing",
};

const BUILT_IN_WHATSAPP_VARIABLES = new Set([
  "nome",
  "evento",
  "event_name",
  "email",
  "user_id",
  "user_access_code",
  "rsvp_link",
  "qrcode_with_user_access_code_url",
  "qrcode_download_url",
]);

const getCampaignTemplateVariables = (template) => {
  const mappedNames = [
    ...(template?.variable_map?.header || []),
    ...(template?.variable_map?.body || []),
  ];
  const componentText = [template?.components?.header?.text, template?.components?.body?.text]
    .filter(Boolean)
    .join(" ");
  const parsedNames = [...componentText.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g)]
    .map((match) => match[1]);
  return [...new Set([...mappedNames, ...parsedNames])]
    .filter((name) => !BUILT_IN_WHATSAPP_VARIABLES.has(name));
};

const formatCampaignPurpose = (purpose) => CAMPAIGN_PURPOSE_LABELS[purpose] || purpose || "WhatsApp";

// Pause reasons recorded by the server for account-wide Meta problems.
const PAUSE_REASON_LABELS = {
  META_131042: "Pagamento da conta WhatsApp pendente na Meta. Regularize a forma de pagamento e retome a campanha.",
  META_131045: "O número não está registrado na Cloud API. Conclua o registro na Meta e retome a campanha.",
  META_133010: "O número não está registrado na Cloud API. Conclua o registro na Meta e retome a campanha.",
  META_131053: "A Meta não conseguiu baixar a imagem do cabeçalho. Verifique a imagem do modelo e retome a campanha.",
  delivery_unknown: "Há envios com resultado incerto aguardando reconciliação.",
};
const describePauseReason = (reason) => PAUSE_REASON_LABELS[reason] || reason || "não informado";

const campaignTemplateSetupReason = (template) => {
  const sendBlockMessages = getWhatsAppTemplateBlockMessages(template);
  if (sendBlockMessages.length > 0) return sendBlockMessages.join("; ");
  if (template.unsupported_reason) return String(template.unsupported_reason).replace(/_/g, " ");
  if (Array.isArray(template.configuration_reasons) && template.configuration_reasons.length > 0) {
    return template.configuration_reasons.map((reason) => String(reason).replace(/_/g, " ")).join("; ");
  }
  return template.is_available === false ? "indisponível na Meta" : "configuração necessária";
};

const defaultSettings = {
  sender_id: "",
  phone_field_input_id: "",
};

const normalizeApiDateTime = (value) => {
  const normalized = String(value || "").trim().replace(" ", "T");
  if (!normalized) return "";
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized) ? normalized : `${normalized}Z`;
};

const toIsoDateTime = (value, timeZone = PANEL_EVENT_TIME_ZONE) => {
  if (!value) return null;
  const wallClockValue = String(value).trim().replace("T", " ");
  const normalizedValue = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(wallClockValue)
    ? `${wallClockValue}:00`
    : wallClockValue;
  const date = zonedTimeToUtc(normalizedValue, timeZone);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toDisplayDate = (value, timeZone = PANEL_EVENT_TIME_ZONE) => {
  if (!value) return "—";
  const date = new Date(normalizeApiDateTime(value));
  return Number.isNaN(date.getTime()) ? "—" : formatInTimeZone(date, timeZone, "dd/MM/yyyy HH:mm");
};


const createCampaignIdempotencyKey = () => {
  const browserCrypto = typeof window !== "undefined" ? window.crypto : null;
  if (typeof browserCrypto?.randomUUID === "function") return browserCrypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getDeliveryCount = (campaign, status) => (
  campaign?.delivery_summary?.[status]
  ?? (status === "pending" ? campaign?.delivery_summary?.queued : undefined)
  ?? campaign?.delivery_metrics?.[status]
  ?? campaign?.[`${status}_count`]
  ?? "—"
);

const notificationError = (notyf, error, fallback) => {
  const code = error?.response?.data?.code;
  const knownMessages = {
    WHATSAPP_PROVIDER_NOT_CONFIGURED: "A integração Meta ainda não foi configurada para este remetente.",
    WHATSAPP_SENDER_UNAVAILABLE: "O remetente selecionado não está ativo ou não está autorizado para este evento.",
    WHATSAPP_META_MESSAGING_PERMISSION_REQUIRED: "O token deste número não tem permissão para enviar mensagens. Peça a um administrador R1 para reconectá-lo.",
    WHATSAPP_META_PERMISSION_MISSING: "A Meta recusou uma operação por falta de permissão. Peça a um administrador R1 para verificar a conexão do número.",
    WHATSAPP_SETTINGS_INVALID: "Selecione um remetente ativo e um campo de celular válido.",
    WHATSAPP_TEMPLATE_INVALID: "Revise os campos e a estrutura do modelo de WhatsApp.",
    WHATSAPP_TEMPLATE_NOT_APPROVED: "Somente modelos aprovados podem ser usados em uma campanha.",
    WHATSAPP_TEMPLATE_UNAVAILABLE: "A Meta não disponibiliza mais este modelo. Sincronize o catálogo antes de tentar novamente.",
    WHATSAPP_TEMPLATE_CONFIGURATION_REQUIRED: "Complete a configuração local do modelo antes de usá-lo em uma campanha.",
    WHATSAPP_TEMPLATE_UNSUPPORTED: "Este formato de modelo ainda não pode ser enviado pela integração R1.",
    WHATSAPP_TEMPLATE_DELETE_IN_PROGRESS: "A exclusão deste modelo na Meta ainda está em andamento. Sincronize o catálogo antes de repetir a ação.",
    WHATSAPP_TEMPLATE_ALREADY_CONFIGURED: "Este modelo da Meta já está configurado no painel. Atualize a lista para abrir a configuração existente.",
    WHATSAPP_TEMPLATE_ARCHIVED: "Este modelo foi arquivado e não aceita novas alterações ou envios.",
    WHATSAPP_TEMPLATE_PENDING_REVIEW: "Este modelo está em análise na Meta e não pode ser alterado até a sincronização concluir.",
    WHATSAPP_TEMPLATE_VARIABLE_INVALID: "O modelo usa uma variável que não é permitida.",
    WHATSAPP_TEMPLATE_FOOTER_VARIABLE_INVALID: "O rodapé do modelo não pode conter variáveis do WhatsApp.",
    WHATSAPP_TEMPLATE_BUTTON_VARIABLE_INVALID: "Em um botão, apenas {{rsvp_link}} pode ser usado como URL dinâmica.",
    RSVP_TEMPLATE_LINK_REQUIRED: "Um modelo RSVP precisa conter {{rsvp_link}} no texto ou em um botão.",
    RSVP_SCHEDULE_AFTER_DEADLINE: "O agendamento de RSVP precisa acontecer antes do prazo de resposta.",
    RSVP_CAPACITY_DEADLINE: "A previsão conservadora da fila não conclui este RSVP antes do prazo configurado.",
    WHATSAPP_SCHEDULE_PAST: "Escolha um horário atual ou futuro para a campanha.",
    RSVP_THROTTLED_PAST_DEADLINE: "O ritmo seguro de envio não permite entregar este RSVP antes do prazo configurado.",
    WHATSAPP_RSVP_PUBLIC_URL_NOT_CONFIGURED: "A URL pública de RSVP ainda não foi configurada no servidor.",
    WHATSAPP_CAMPAIGN_RECONCILIATION_REQUIRED: "Há envios com resultado incerto. Reconcilie-os antes de retomar esta campanha.",
    WHATSAPP_CAMPAIGN_SNAPSHOT_MISSING: "Esta campanha antiga não tem as configurações salvas necessárias para um reenvio seguro.",
    WHATSAPP_RESEND_ALREADY_QUEUED: "Já existe um reenvio ativo ou concluído para uma ou mais pessoas selecionadas.",
    WHATSAPP_RESEND_DELIVERY_INELIGIBLE: "Somente entregas que falharam podem ser selecionadas para reenvio.",
    WHATSAPP_RESEND_RECIPIENT_ALREADY_ACTIVE: "Já existe um reenvio ativo para estas pessoas.",
    WHATSAPP_WEBHOOK_NOT_READY: "A conexão de eventos do WhatsApp ainda não está pronta para envio.",
    WHATSAPP_WEBHOOK_MISCONFIGURED: "O webhook do WhatsApp no app da Meta não está configurado para este servidor, então as respostas do RSVP não chegariam. Veja o aviso em Configurações do sistema.",
    WHATSAPP_WEBHOOK_SILENT: "A Meta não está entregando as notificações deste número, então as respostas do RSVP não chegariam. Tente novamente quando o aviso em Configurações do sistema sumir.",
    WHATSAPP_EVIDENCE_REQUIRED: "Registre uma evidência com pelo menos 10 caracteres para auditar esta alteração.",
    WHATSAPP_GLOBAL_SUPPRESSION_ADMIN_REQUIRED: "Somente um administrador R1 pode alterar bloqueios globais do remetente.",
    WHATSAPP_CAPACITY_UNKNOWN: "A capacidade atual do número ainda não foi consultada. Atualize o remetente no WhatsApp Manager.",
    WHATSAPP_QUALITY_LOW: "A qualidade atual do remetente exige pausa operacional antes de novas campanhas.",
    WHATSAPP_CAPACITY_EXCEEDED: "A audiência ultrapassa a capacidade atualmente disponível para o número.",
    WHATSAPP_PHONE_HASH_SECRET_NOT_CONFIGURED: "A chave interna de privacidade do WhatsApp ainda não foi configurada no servidor.",
    WHATSAPP_NO_ELIGIBLE_RECIPIENTS: "Nenhum participante elegível foi encontrado. Verifique telefone e supressões.",
    WHATSAPP_TEMPLATE_BINDINGS_INVALID: "Associe cada variável do modelo a um campo de texto válido deste evento.",
    WHATSAPP_TEMPLATE_VARIABLE_MISSING: "Há participantes sem dados necessários para preencher o modelo.",
    WHATSAPP_AUDIENCE_INVALID: "A seleção manual contém um participante inativo ou de outro evento.",
    RSVP_SETTINGS_INCOMPLETE: "Configure as respostas e um prazo futuro para o RSVP.",
    RSVP_STORAGE_MIGRATION_REQUIRED: "As respostas existentes deste evento precisam ser atualizadas antes de usar o RSVP.",
    RSVP_LEGACY_SNAPSHOT_REVIEW_REQUIRED: "Os dados salvos desta campanha precisam de revisão antes de continuar.",
    RSVP_WHATSAPP_PAGE_REQUIRED: "Este RSVP tem respostas personalizadas. Escolha um modelo que abre a página de confirmação pelo link RSVP.",
    RSVP_WHATSAPP_ASSOCIATIONS_REQUIRED: "Associe os botões deste modelo às respostas na seção WhatsApp da configuração do RSVP. Se o modelo mudou, associe a versão atual.",
    RSVP_SNAPSHOT_INCOMPLETE: "Revise as opções e o prazo do RSVP antes de criar o envio.",
    RSVP_CHOICES_INVALID: "Revise os valores das respostas do RSVP.",
    RSVP_FIELD_NOT_ALLOWED: "Selecione um campo de RSVP compatível e diferente do campo de telefone.",
    PREVIEW_STALE: "A prévia ficou desatualizada. Gere uma nova prévia antes de criar a campanha.",
  };
  notyf.open({
    type: "danger",
    message: knownMessages[code] || fallback,
    ripple: true,
    dismissible: true,
  });
};

const StatusBadge = ({ status, config }) => {
  const item = config[status] || { label: status || "Indefinido", bg: "secondary" };
  return <Badge bg={item.bg}>{item.label}</Badge>;
};

const CampaignModal = ({ show, onHide, eventId, eventTimezone, registrationEndDate, settings, senders, templates, templateVariableFields, rsvpRegistry, onRsvpRegistryChange, onCreated, individual = false, configurationReady, configurationContent }) => {
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const userState = useAuth();
  const canManageRsvps = canAccessEventRsvps(userState, 2);
  const availableRsvps = rsvpRegistry?.definitions || [];
  const defaultRsvpId = availableRsvps.find((item) => item.definition_id === rsvpRegistry?.attendance_definition_id)?.definition_id || availableRsvps[0]?.definition_id || "";
  const selectedAccountTemplates = useMemo(
    () => {
      const selectedSender = senders.find((sender) => String(sender.sender_id) === String(settings?.sender_id));
      return templates.filter((template) => {
        const sameAccount = template.account_id != null && selectedSender?.account_id != null
          ? String(template.account_id) === String(selectedSender.account_id)
          : template.waba_id && selectedSender?.waba_id
            ? String(template.waba_id) === String(selectedSender.waba_id)
            : String(template.sender_id) === String(settings?.sender_id);
        return sameAccount && !isDeletedWhatsAppTemplate(template);
      });
    },
    [senders, settings?.sender_id, templates],
  );
  const approvedTemplates = useMemo(() => selectedAccountTemplates.filter((template) => (
    template.sendable === true
    && template.archived !== true
    && template.configuration_status === "ready"
    && template.is_available !== false
    && template.is_supported !== false
    && template.status === "approved"
  )), [selectedAccountTemplates]);
  const blockedTemplates = useMemo(() => selectedAccountTemplates.filter((template) => (
    template.status === "approved"
    && !approvedTemplates.some((available) => String(available.template_id) === String(template.template_id))
  )), [approvedTemplates, selectedAccountTemplates]);
  const templateOptions = useMemo(
    () => approvedTemplates.map((template) => ({
      value: String(template.template_id),
      label: `${template.internal_title || `Modelo #${template.template_id}`} · ${formatCampaignPurpose(template.purpose)}`,
      template,
    })),
    [approvedTemplates],
  );
  const [form, setForm] = useState({ name: "", template_id: "", rsvp_definition_id: "", audience_mode: "all", user_ids: "", scheduled_for: "", template_variables: {} });
  const [preview, setPreview] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantPage, setParticipantPage] = useState(1);
  const [participants, setParticipants] = useState([]);
  const [participantsTotal, setParticipantsTotal] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showRsvpManager, setShowRsvpManager] = useState(false);
  const draftSessionRef = useRef(null);

  useEffect(() => {
    if (!show) {
      draftSessionRef.current = null;
      setShowRsvpManager(false);
      return;
    }
    const sessionKey = `${eventId}:${individual ? "individual" : "campaign"}`;
    if (draftSessionRef.current !== sessionKey) {
      draftSessionRef.current = sessionKey;
      setForm({ name: "", template_id: templateOptions[0]?.value || "", rsvp_definition_id: defaultRsvpId, audience_mode: individual ? "manual" : "all", user_ids: "", scheduled_for: "", template_variables: {} });
      setPreview(null);
      setIdempotencyKey(createCampaignIdempotencyKey());
      setSelectedUsers([]);
      setParticipantSearch("");
      setParticipantPage(1);
    }
  }, [show, templateOptions, defaultRsvpId, eventId, individual]);

  useEffect(() => {
    if (!show) return;
    setForm((current) => {
      const templateId = current.template_id || templateOptions[0]?.value || "";
      const rsvpId = current.rsvp_definition_id || defaultRsvpId;
      return templateId === current.template_id && rsvpId === current.rsvp_definition_id
        ? current : { ...current, template_id: templateId, rsvp_definition_id: rsvpId };
    });
  }, [defaultRsvpId, show, templateOptions]);

  useEffect(() => { setPreview(null); }, [configurationReady, settings?.sender_id, settings?.phone_field_input_id]);

  useEffect(() => {
    let active = true;
    if (!show || form.audience_mode !== "manual") return undefined;
    const timer = setTimeout(async () => {
      try {
        const response = await getWhatsAppParticipants({ eventId, search: participantSearch, page: participantPage, pageSize: 50 });
        if (active) {
          setParticipants(response.data?.participants || []);
          setParticipantsTotal(response.data?.total || 0);
        }
      } catch (_) {
        if (active) setParticipants([]);
      }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [eventId, form.audience_mode, participantPage, participantSearch, show]);

  const changeDraft = (updater) => {
    setForm(updater);
    setPreview(null);
    setIdempotencyKey(createCampaignIdempotencyKey());
  };

  const selectedTemplate = templateOptions.find((option) => option.value === String(form.template_id))?.template;
  const requiresRsvp = selectedTemplate?.purpose === "rsvp";
  const rsvpSelectionValid = !requiresRsvp || availableRsvps.some((item) => item.definition_id === form.rsvp_definition_id);
  const customTemplateVariables = getCampaignTemplateVariables(selectedTemplate);
  const templateVariablesComplete = customTemplateVariables.every((name) => Boolean(form.template_variables[name]));
  const recipientValid = individual ? selectedUsers.length === 1
    : form.audience_mode !== "manual" || selectedUsers.length > 0 || Boolean(form.user_ids.trim());
  const readyToPreview = configurationReady && Boolean(form.template_id) && templateVariablesComplete && rsvpSelectionValid
    && recipientValid && (individual || Boolean(form.name.trim()));
  const canLaunchPreview = preview?.can_launch && (!individual || Number(preview.eligible_count) === 1);

  const payload = () => ({
    event_id: eventId,
    name: individual ? `Mensagem individual · Participante #${selectedUsers[0]}` : form.name.trim(),
    sender_id: settings?.sender_id || null,
    template_id: Number(form.template_id),
    ...(requiresRsvp ? { rsvp_definition_id: form.rsvp_definition_id } : {}),
    audience: {
      mode: individual ? "manual" : form.audience_mode,
      user_ids: individual ? selectedUsers : form.audience_mode === "manual"
        ? (selectedUsers.length ? selectedUsers : form.user_ids.split(/[\s,;]+/).map(Number).filter((id) => Number.isInteger(id) && id > 0))
        : [],
    },
    scheduled_for: individual ? null : toIsoDateTime(form.scheduled_for, eventTimezone),
    template_variables: Object.fromEntries(customTemplateVariables
      .filter((name) => form.template_variables[name])
      .map((name) => [name, { source: "event_field", input_id: String(form.template_variables[name]) }])),
  });

  const generatePreview = async () => {
    if (!readyToPreview) return;
    setLoading(true);
    try {
      const response = await previewWhatsAppCampaign(payload());
      setPreview(response.data);
    } catch (error) {
      notificationError(notyf, error, "Não foi possível calcular a prévia da campanha.");
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!preview?.preview_hash || !readyToPreview || !canLaunchPreview) return;
    const requestIdempotencyKey = idempotencyKey || createCampaignIdempotencyKey();
    if (!idempotencyKey) setIdempotencyKey(requestIdempotencyKey);
    setLoading(true);
    try {
      await createWhatsAppCampaign({ ...payload(), preview_hash: preview.preview_hash, idempotency_key: requestIdempotencyKey });
      notyf.open({ type: "success", message: individual ? "Mensagem incluída na fila de envio." : "Campanha criada. O envio será feito pela fila quando estiver autorizada.", ripple: true, dismissible: true });
      await onCreated();
      onHide();
    } catch (error) {
      notificationError(notyf, error, individual ? "Não foi possível enviar a mensagem." : "Não foi possível criar a campanha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal show={show && !showRsvpManager} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>{individual ? "Enviar mensagem de WhatsApp" : "Nova campanha de WhatsApp"}</Modal.Title></Modal.Header>
      <Modal.Body>
        {configurationContent}
        {blockedTemplates.length > 0 && <Alert variant="warning" className="p-2 d-block" style={{ minWidth: 0 }}>
          {blockedTemplates.length === 1
            ? "Um modelo aprovado da conta está bloqueado para uso nesta campanha."
            : `${blockedTemplates.length} modelos aprovados da conta estão bloqueados para uso nesta campanha.`}
          <ul className="mb-0 ps-3">{blockedTemplates.slice(0, 8).map((template) => <li key={template.template_id || template.catalog_id}>{template.internal_title || template.name || template.provider_template_name || `Modelo #${template.template_id || template.catalog_id}`} — {campaignTemplateSetupReason(template)}</li>)}</ul>
        </Alert>}
        {!configurationReady ? <Alert variant="primary" className="p-2 mb-0">Salve o remetente e o campo de telefone acima para escolher o modelo e o destinatário.</Alert> : approvedTemplates.length === 0 ? (
          <Alert variant="primary" className="p-2 mb-0">Nenhum modelo aprovado está disponível para este evento e remetente.</Alert>
        ) : (
          <>
            {!individual && <Form.Group className="mb-3">
              <Form.Label>Nome da campanha/lote</Form.Label>
              <Form.Control value={form.name} maxLength={100} onChange={(event) => changeDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Ex.: Aviso de alteração de horário" />
            </Form.Group>}
            <Form.Group className="mb-3">
              <Form.Label>Modelo aprovado</Form.Label>
              <Select
                inputId="whatsapp-campaign-template"
                options={templateOptions}
                value={templateOptions.find((option) => option.value === String(form.template_id)) || null}
                onChange={(option) => changeDraft((current) => ({ ...current, template_id: option?.value || "", template_variables: {} }))}
                placeholder="Selecione um modelo aprovado"
                isClearable={false}
                noOptionsMessage={() => "Nenhum modelo aprovado disponível"}
              />
            </Form.Group>
            {requiresRsvp && <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1 flex-wrap">
                <Form.Label className="mb-0" htmlFor="whatsapp-campaign-rsvp">RSVP desta campanha</Form.Label>
                {canManageRsvps && <Button size="sm" variant="outline-primary" onClick={() => setShowRsvpManager(true)}><FontAwesomeIcon icon={faCog} className="me-1" />Gerenciar RSVPs</Button>}
              </div>
              <Form.Select id="whatsapp-campaign-rsvp" value={form.rsvp_definition_id} onChange={(event) => changeDraft((current) => ({ ...current, rsvp_definition_id: event.target.value }))}>
                <option value="">Selecione um RSVP</option>
                {(rsvpRegistry?.definitions || []).map((item) => <option key={item.definition_id} value={item.definition_id}>{item.name}{item.definition_id === rsvpRegistry.attendance_definition_id ? " · Confirmação de participação" : ""}</option>)}
              </Form.Select>
              {!form.rsvp_definition_id && <Form.Text>Configure um RSVP do evento antes de gerar a prévia.</Form.Text>}
              {selectedTemplate?.components?.buttons?.some((button) => button.action) && <Form.Text className="d-block">Os botões deste modelo usam os valores associados na seção WhatsApp da configuração do RSVP. A prévia valida essas associações antes do envio.</Form.Text>}
            </Form.Group>}
            {customTemplateVariables.length > 0 && (
              <div className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">Campos do modelo</div>
                <div className="small text-muted mb-3">Associe cada variável a um campo preenchido pelos participantes.</div>
                {customTemplateVariables.map((name) => {
                  const fieldOptions = templateVariableFields.map((field) => ({
                    value: String(field.inputID),
                    label: field.label || String(field.inputID),
                  }));
                  return (
                    <Form.Group className="mb-3" key={name}>
                      <Form.Label>{`{{${name}}}`} <span className="text-danger">*</span></Form.Label>
                      <Select
                        inputId={`whatsapp-campaign-variable-${name}`}
                        options={fieldOptions}
                        value={fieldOptions.find((option) => option.value === String(form.template_variables[name])) || null}
                        onChange={(option) => changeDraft((current) => ({
                          ...current,
                          template_variables: { ...current.template_variables, [name]: option?.value || "" },
                        }))}
                        placeholder="Selecione um campo do evento"
                        isClearable
                        noOptionsMessage={() => "Nenhum campo disponível"}
                      />
                    </Form.Group>
                  );
                })}
                {!templateVariablesComplete && <div className="small text-danger">Selecione um campo para cada variável antes de gerar a prévia.</div>}
              </div>
            )}
            <Form.Group className="mb-2">
              <Form.Label>{individual ? "Destinatário" : "Destinatários"}</Form.Label>
              {!individual && <>
                <Form.Check type="radio" id="whatsapp-audience-all" label="Todos os participantes ativos elegíveis" checked={form.audience_mode === "all"} onChange={() => changeDraft((current) => ({ ...current, audience_mode: "all" }))} />
                <Form.Check type="radio" id="whatsapp-audience-manual" label="Escolher participantes individualmente ou em lote" checked={form.audience_mode === "manual"} onChange={() => changeDraft((current) => ({ ...current, audience_mode: "manual" }))} />
              </>}
            </Form.Group>
            {(individual || form.audience_mode === "manual") && <div className="border rounded p-2 mb-3">
              <Form.Control className="mb-2" value={participantSearch} onChange={(event) => { setParticipantPage(1); setParticipantSearch(event.target.value); }} placeholder="Buscar participante por nome ou telefone" aria-label="Buscar participantes" />
              <div className="small text-muted mb-2">{selectedUsers.length} selecionado(s) · {participantsTotal} resultado(s). Telefone, supressões e demais regras da campanha são verificados antes do envio.</div>
              <div style={{ maxHeight: 190, overflowY: "auto" }}>
                {participants.map((participant) => <Form.Check key={participant.user_id} className="border-bottom py-1" type={individual ? "radio" : "checkbox"} name={individual ? "whatsapp-individual-recipient" : undefined} id={`whatsapp-user-${participant.user_id}`} checked={selectedUsers.includes(Number(participant.user_id))} onChange={(event) => {
                  const id = Number(participant.user_id);
                  setSelectedUsers((current) => individual ? [id] : event.target.checked ? [...new Set([...current, id])] : current.filter((value) => value !== id));
                  changeDraft((current) => ({ ...current, user_ids: "" }));
                }} label={<span>{participant.name || `Participante #${participant.user_id}`} <span className="text-muted">· {participant.masked_phone || "telefone não informado"}</span>{participant.suppressed && <Badge bg="dark" className="ms-2">suprimido</Badge>}</span>} />)}
                {participants.length === 0 && <div className="small text-muted py-2">Nenhum participante encontrado.</div>}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2"><span className="small text-muted">Página {participantPage} de {Math.max(1, Math.ceil(participantsTotal / 50))}</span><div className="d-flex gap-2"><Button size="sm" variant="outline-secondary" disabled={participantPage <= 1} onClick={() => setParticipantPage((value) => value - 1)}>Anterior</Button><Button size="sm" variant="outline-secondary" disabled={participantPage * 50 >= participantsTotal} onClick={() => setParticipantPage((value) => value + 1)}>Próxima</Button></div></div>
              {!individual && <Form.Control className="mt-2" value={form.user_ids} onChange={(event) => { setSelectedUsers([]); changeDraft((current) => ({ ...current, user_ids: event.target.value })); }} placeholder="Ou informe IDs manualmente: 120, 121" />}
            </div>}
            {!individual && <Form.Group className="mb-3">
              <Form.Label>Agendar para <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control type="datetime-local" value={form.scheduled_for} onChange={(event) => changeDraft((current) => ({ ...current, scheduled_for: event.target.value }))} />
            </Form.Group>}
            {preview && (
                <Alert variant={preview.can_launch ? "success" : "primary"} className="p-2 mb-0 d-block" style={{ minWidth: 0 }}>
                  <div className="fw-semibold">{individual ? "Prévia da mensagem" : "Prévia da campanha"}</div>
                  <div>{preview.eligible_count || 0} elegíveis · {preview.excluded_count || 0} excluídos</div>
                  {preview.presence_settings?.version_id != null && <div className="small mt-1">RSVP · versão #{preview.presence_settings.version_id}</div>}
                  {preview.presence_settings?.presentation_revision != null && <div className="small text-muted">Apresentação RSVP · revisão {preview.presence_settings.presentation_revision}</div>}
                  {preview.capacity && <div className="small mt-1">Capacidade observada: {preview.capacity.available ?? "indisponível"} contato(s) disponível(is) na janela de 24 horas do agendamento{preview.capacity.new_contacts_needed !== undefined ? ` · ${preview.capacity.new_contacts_needed} novo(s) contato(s) desta campanha contam para a capacidade.` : ""}</div>}
                  {preview.timeline && <div className="small mt-1">Previsão conservadora da fila: até {toDisplayDate(preview.timeline.estimated_completion_at, eventTimezone)} · {preview.timeline.queued_recipients || 0} destinatário(s) antes desta campanha · ritmo técnico de até {preview.timeline.rate_per_minute} envio(s)/minuto.</div>}
                  {Object.keys(preview.excluded || {}).length > 0 && <div className="small mt-2"><strong>Exclusões:</strong><ul className="mb-0 ps-3">{Object.entries(preview.excluded).map(([reason, count]) => <li key={reason}>{count} × {AUDIENCE_EXCLUSION_LABELS[reason] || reason}</li>)}</ul></div>}
                  {Array.isArray(preview.samples) && preview.samples.length > 0 && <div className="mt-3"><strong>Amostra da mensagem renderizada</strong>{preview.samples.slice(0, 3).map((sample) => <Card key={sample.user_id} className="mt-2"><Card.Body className="p-2"><div className="small fw-semibold">{sample.name} · {sample.masked_phone}</div>{sample.header && <div className="small fw-semibold">{sample.header}</div>}<div className="small" style={{ whiteSpace: "pre-wrap" }}>{sample.body}</div>{sample.footer && <div className="small text-muted">{sample.footer}</div>}</Card.Body></Card>)}</div>}
                  {preview.estimated_cost && <div className="small mt-2"><strong>Estimativa de custo:</strong> {preview.estimated_cost}. O valor final depende da categoria, país e cobrança vigente da Meta.</div>}
                  {preview.cost_estimate?.status === "unavailable" && <div className="small mt-2"><strong>Estimativa de custo:</strong> indisponível até a operação registrar uma leitura atual da precificação da Meta. O envio não assume preço fixo.</div>}
                  {!preview.can_launch && <div className="small mt-1">{preview.blocking_reason || "A campanha não pode ser criada ainda."}</div>}
                </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        {approvedTemplates.length > 0 && <Button variant="outline-primary" disabled={!readyToPreview} onClick={generatePreview}><FontAwesomeIcon icon={faCircleInfo} className="me-2" />Gerar prévia</Button>}
        {preview && <Button variant="primary" disabled={!canLaunchPreview || !readyToPreview} onClick={createCampaign}><FontAwesomeIcon icon={faPaperPlane} className="me-2" />{individual ? "Enviar mensagem" : "Criar campanha"}</Button>}
      </Modal.Footer>
    </Modal>
    {canManageRsvps && <EventRsvpManagerModal
      show={show && showRsvpManager}
      onHide={() => setShowRsvpManager(false)}
      eventId={eventId}
      eventTimezone={eventTimezone}
      registrationEndDate={registrationEndDate}
      onRegistryChange={onRsvpRegistryChange}
      onSaved={() => { setPreview(null); setIdempotencyKey(createCampaignIdempotencyKey()); }}
    />}
    </>
  );
};

const WhatsAppDeliveryDetails = ({ show, delivery, eventId, eventTimezone, onHide, onResolved }) => {
  const [record, setRecord] = useState(null);
  const [evidence, setEvidence] = useState("");
  const [providerId, setProviderId] = useState("");
  const [resolution, setResolution] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(Boolean(show && delivery?.delivery_id));
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setRecord(null);
    setEvidence("");
    setProviderId("");
    setResolution("");
    setError("");
    setSaving(false);
    setLoadError(false);
    setLoadingDetails(Boolean(show && delivery?.delivery_id));
    if (!show || !delivery?.delivery_id) return undefined;
    getWhatsAppDelivery({ deliveryId: delivery.delivery_id, eventId })
      .then((response) => { if (active) setRecord(response.data || {}); })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoadingDetails(false); });
    return () => { active = false; };
  }, [delivery, eventId, show]);

  const current = record?.delivery || delivery;
  const message = current?.rendered_message;
  const hasSavedMessage = Boolean(message && (message.header || message.body || message.footer || message.image_url || message.buttons?.length));
  const attempts = Array.isArray(record?.attempts) ? record.attempts : [];
  const providerEvents = Array.isArray(record?.provider_events) ? record.provider_events : [];
  const rsvpFeedback = Array.isArray(record?.rsvp_feedback) ? record.rsvp_feedback : [];
  const audit = Array.isArray(record?.audit) ? record.audit : [];
  const evidenceValid = evidence.trim().length >= 10 && evidence.trim().length <= 1000;

  const resolve = async () => {
    if (!show || !delivery?.delivery_id || current?.status !== "unknown" || loadingDetails || loadError || saving || !resolution || !evidenceValid) return;
    setSaving(true); setError("");
    try { await resolveWhatsAppDelivery({ deliveryId: delivery.delivery_id, eventId, resolution, evidence: evidence.trim(), providerMessageId: providerId.trim() }); await onResolved(); }
    catch (requestError) { setError(requestError?.response?.data?.message || "Não foi possível salvar a decisão auditada."); }
    finally { setSaving(false); }
  };

  return <Modal show={show} onHide={onHide} centered scrollable>
    <Modal.Header closeButton><Modal.Title>Entrega #{delivery?.delivery_id}</Modal.Title></Modal.Header><Modal.Body>
      <div className="mb-2">{current?.name || `Participante #${current?.user_id || "—"}`} · {current?.masked_phone || "telefone oculto"} · <StatusBadge status={current?.status} config={DELIVERY_STATUS} /></div>
      <div className="small">Tentativas: {current?.attempt_count ?? record?.attempts?.length ?? "—"} · Código: {current?.error_code || "—"}</div>
      {current?.error_detail && <Alert variant="secondary" className="small p-2 mt-2">{current.error_detail}</Alert>}
      {loadingDetails && <div className="small text-muted mt-3">Carregando conteúdo e histórico da entrega...</div>}
      {loadError && <Alert variant="danger" className="p-2 mt-3">Não foi possível carregar os detalhes. Feche e abra a entrega novamente para consultar o histórico e registrar uma decisão.</Alert>}
      {!loadingDetails && !loadError && <>
        <h6 className="mt-3">Mensagem registrada no envio</h6>
        {hasSavedMessage ? <div className="border rounded p-3 small" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {message.header && <div className="fw-semibold mb-2">{message.header}</div>}
          {message.body && <div>{message.body}</div>}
          {message.footer && <div className="text-muted mt-2">{message.footer}</div>}
          {message.image_url && <div className="mt-2">Imagem: {String(message.image_url).startsWith("https://") ? <a href={message.image_url} target="_blank" rel="noreferrer">Ver imagem registrada</a> : message.image_url}</div>}
          {Array.isArray(message.buttons) && message.buttons.map((button, index) => <div key={index} className="border-top mt-2 pt-2"><strong>{button.text || "Botão"}</strong>{button.url && <div>{button.url}</div>}</div>)}
        </div> : <div className="small text-muted">Nenhum conteúdo de envio registrado para esta entrega.</div>}

        <h6 className="mt-3">Tentativas de envio</h6>
        {attempts.length ? attempts.map((attempt) => <div key={attempt.attempt_id || attempt.attempt_number} className="border rounded p-2 mb-2 small">
          <strong>Tentativa {attempt.attempt_number}</strong> · <StatusBadge status={attempt.status} config={DELIVERY_STATUS} />
          <div>Início: {toDisplayDate(attempt.submitted_at, eventTimezone)} · Conclusão: {toDisplayDate(attempt.completed_at, eventTimezone)}</div>
          {attempt.provider_message_id && <div style={{ overflowWrap: "anywhere" }}>ID na Meta: {attempt.provider_message_id}</div>}
          {attempt.error_code && <div>Código: {attempt.error_code}</div>}
          {attempt.error_detail && <div>{attempt.error_detail}</div>}
        </div>) : <div className="small text-muted">Nenhuma tentativa de envio registrada.</div>}

        <h6 className="mt-3">Atualizações da Meta</h6>
        {providerEvents.length ? providerEvents.map((entry) => <div key={entry.provider_event_id} className="border rounded p-2 mb-2 small">
          <StatusBadge status={entry.event_status === "sent" ? "accepted" : entry.event_status} config={DELIVERY_STATUS} />
          <div>Informado pela Meta: {toDisplayDate(entry.provider_event_at, eventTimezone)}</div>
          <div>Recebido: {toDisplayDate(entry.created_at, eventTimezone)} · Processado: {toDisplayDate(entry.processed_at, eventTimezone)}</div>
          {entry.provider_message_id && <div style={{ overflowWrap: "anywhere" }}>ID na Meta: {entry.provider_message_id}</div>}
          {Array.isArray(entry.event_detail?.errors) && entry.event_detail.errors.map((providerError, index) => <div key={index} className="mt-1">{[providerError.code, providerError.title, providerError.message, providerError.details].filter(Boolean).join(" · ")}</div>)}
        </div>) : <div className="small text-muted">Nenhuma atualização da Meta registrada.</div>}

        {rsvpFeedback.length > 0 && <>
          <h6 className="mt-4">Retornos do RSVP no WhatsApp</h6>
          <p className="small text-muted">Últimos 100 retornos desta entrega. O aceite da Meta não confirma a entrega ao participante. Uma falha no retorno não desfaz a resposta registrada.</p>
          {rsvpFeedback.map((feedback) => <div key={feedback.response_revision_id} className="border rounded p-3 mb-3 small">
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2"><StatusBadge status={feedback.state} config={RSVP_FEEDBACK_STATUS} /><span>{feedback.language || "Idioma não registrado"}</span></div>
            {feedback.answer_label && <div>Resposta selecionada: {feedback.answer_label}</div>}
            <div className="my-2" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{feedback.body}</div>
            <div>Tentativas: {feedback.attempts || 0} · Criado: {toDisplayDate(feedback.created_at, eventTimezone)}</div>
            {feedback.accepted_at && <div>Aceite pela Meta: {toDisplayDate(feedback.accepted_at, eventTimezone)}</div>}
            {feedback.delivered_at && <div>Entregue: {toDisplayDate(feedback.delivered_at, eventTimezone)}</div>}
            {feedback.read_at && <div>Lido: {toDisplayDate(feedback.read_at, eventTimezone)}</div>}
            {feedback.failed_at && <div>Falha informada: {toDisplayDate(feedback.failed_at, eventTimezone)}</div>}
            {feedback.error_code && <div>Código: {feedback.error_code}</div>}
            {feedback.provider_message_id && <div style={{ overflowWrap: "anywhere" }}>ID do retorno na Meta: {feedback.provider_message_id}</div>}
            {feedback.provider_events.map((entry) => Array.isArray(entry.event_detail?.errors) && entry.event_detail.errors.map((error, index) => <div key={`${entry.provider_event_id}:${index}`} className="mt-1">{[error.code, error.title, error.message, error.details].filter(Boolean).join(" · ")}</div>))}
          </div>)}
        </>}

        <h6 className="mt-3">Decisões e evidências</h6>
        {audit.length ? audit.map((entry) => <div key={entry.operation_id} className="border rounded p-2 mb-2 small">
          <strong>{entry.action === "resolve_unknown_confirmed_sent" ? "Confirmado pelo operador: enviado" : entry.action === "resolve_unknown_confirmed_not_sent" ? "Confirmado pelo operador: não enviado" : "Atualização da entrega"}</strong>
          <div>{toDisplayDate(entry.created_at, eventTimezone)} · {entry.actor_id ? `Operador #${entry.actor_id}` : "Sistema"}</div>
          {entry.evidence && <div className="mt-1" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{entry.evidence}</div>}
          {entry.details?.providerMessageId && <div style={{ overflowWrap: "anywhere" }}>ID na Meta: {entry.details.providerMessageId}</div>}
        </div>) : <div className="small text-muted">Nenhuma decisão de recuperação registrada.</div>}
      </>}
      {current?.status === "unknown" && !loadingDetails && !loadError && <div className="border rounded p-3 mt-3">
        <Alert variant="warning" className="small p-2">A Meta pode ter recebido a mensagem apesar da resposta incerta. Reenviar pode duplicar. Escolha explicitamente a resolução e registre a evidência consultada.</Alert>
        {error && <Alert variant="danger" className="p-2">{error}</Alert>}
        <Form.Select className="mb-2" aria-label="Conclusão da verificação" value={resolution} disabled={saving} onChange={(event) => { setResolution(event.target.value); if (event.target.value !== "confirmed_sent") setProviderId(""); }}>
          <option value="">Escolha a conclusão</option><option value="confirmed_not_sent">Confirmado: não foi enviado</option><option value="confirmed_sent">Confirmado: foi enviado</option>
        </Form.Select>
        {resolution === "confirmed_sent" && <Form.Control className="mb-2" aria-label="ID da mensagem na Meta" value={providerId} maxLength={192} disabled={saving} onChange={(event) => setProviderId(event.target.value)} placeholder="ID da mensagem na Meta, se disponível" />}
        <Form.Control as="textarea" rows={3} aria-label="Evidência da verificação" value={evidence} maxLength={1000} disabled={saving} onChange={(event) => setEvidence(event.target.value)} placeholder="Evidência e procedimento de verificação (mínimo de 10 caracteres)" />
        <Button className="mt-2" variant="warning" disabled={!resolution || !evidenceValid || saving} onClick={resolve}>Registrar decisão auditada</Button>
      </div>}
    </Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Fechar</Button></Modal.Footer>
  </Modal>;
};

const CampaignDetailsModal = ({ show, onHide, eventId, eventTimezone, campaign }) => {
  const { setLoading } = useLayout();
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deliveryToInspect, setDeliveryToInspect] = useState(null);
  const [resendIds, setResendIds] = useState([]);
  const [resendPreview, setResendPreview] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendError, setResendError] = useState("");
  const pageSize = 20;

  useEffect(() => {
    let active = true;
    if (!show || !campaign?.campaign_id) {
      setDetail(null);
      setLoadError(false);
      return undefined;
    }

    const loadDetails = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await getWhatsAppCampaign({ campaignId: campaign.campaign_id, eventId, page, pageSize, search, status: statusFilter || undefined });
        if (active) setDetail(response.data || {});
      } catch (_) {
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDetails();
    return () => {
      active = false;
      setLoading(false);
    };
  }, [campaign?.campaign_id, eventId, page, pageSize, search, setLoading, show, statusFilter]);

  const currentCampaign = detail?.campaign || campaign;
  const deliveries = Array.isArray(detail?.deliveries) ? detail.deliveries : [];
  const summary = detail?.delivery_summary || detail?.delivery_metrics || {};
  const rsvpSummary = detail?.rsvp_summary;
  useEffect(() => { setTotal(detail?.pagination?.total ?? detail?.total ?? 0); }, [detail?.pagination?.total, detail?.total]);

  const previewResend = async (failedOnly) => {
    if (!show || !campaign?.campaign_id || resendBusy) return;
    setResendBusy(true); setResendError("");
    setResendPreview(null);
    try {
      const response = await previewWhatsAppResend({ campaignId: campaign.campaign_id, payload: { event_id: eventId, failed_only: failedOnly, delivery_ids: failedOnly ? [] : resendIds } });
      setResendPreview({ ...response.data, failedOnly });
    } catch (error) { setResendError(error?.response?.data?.message || "Não foi possível pré-visualizar os reenvios."); }
    finally { setResendBusy(false); }
  };
  const executeResend = async () => {
    if (!show || !campaign?.campaign_id || !resendPreview?.preview_hash || resendBusy) return;
    setResendBusy(true);
    try {
      await resendWhatsAppDeliveries({ campaignId: campaign.campaign_id, payload: { event_id: eventId, delivery_ids: resendPreview.failedOnly ? [] : resendIds, failed_only: resendPreview.failedOnly, preview_hash: resendPreview.preview_hash, idempotency_key: createCampaignIdempotencyKey() } });
      setResendPreview(null); setResendIds([]);
      const response = await getWhatsAppCampaign({ campaignId: campaign.campaign_id, eventId, page, pageSize, search, status: statusFilter || undefined }); setDetail(response.data || {});
    } catch (error) { setResendError(error?.response?.data?.message || "Não foi possível solicitar o reenvio."); }
    finally { setResendBusy(false); }
  };

  if (!show || !campaign?.campaign_id) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton><Modal.Title>Histórico da campanha #{campaign?.campaign_id}</Modal.Title></Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="primary" className="p-2 mb-3">O histórico detalhado ainda não está disponível. A campanha continua listada acima; atualize após a integração do servidor central.</Alert>}
        {currentCampaign && <Row className="mb-3">
          <Col md={6}><div className="small text-muted">Lote</div><div>{currentCampaign.name || `Campanha #${currentCampaign.campaign_id}`}</div></Col>
          <Col md={6}><div className="small text-muted">Modelo</div><div>{currentCampaign.template_title || `Modelo #${currentCampaign.template_id}`}</div>{currentCampaign.template_version != null && <div className="small text-muted">Versão salva: {formatWhatsAppTemplateVersion(currentCampaign.template_version)}</div>}</Col>
          <Col md={3}><div className="small text-muted">Finalidade</div><div>{formatCampaignPurpose(currentCampaign.purpose)}</div></Col>
          <Col md={3}><div className="small text-muted">Estado</div><StatusBadge status={currentCampaign.status} config={CAMPAIGN_STATUS} />{currentCampaign.status === "paused" && <div className="small text-muted mt-1">Motivo: {describePauseReason(currentCampaign.pause_reason)}</div>}</Col>
        </Row>}
        {currentCampaign?.purpose === "rsvp" && (currentCampaign.rsvp_version_id != null || currentCampaign.rsvp_deadline_at) && <div className="small text-muted mb-3">RSVP{currentCampaign.rsvp_version_id != null ? ` · versão #${currentCampaign.rsvp_version_id}` : ""}{currentCampaign.rsvp_deadline_at ? ` · prazo ${toDisplayDate(currentCampaign.rsvp_deadline_at, eventTimezone)}` : ""}</div>}
        <Row className="g-2 mb-3">
          {[
            ["accepted", "Aceites"],
            ["delivered", "Entregues"],
            ["read", "Lidos"],
            ["failed", "Falhas"],
            ["unknown", "Resultado incerto"], ["pending", "Pendentes"], ["suppressed", "Suprimidos"],
          ].map(([status, label]) => <Col key={status} xs={6} md><Card className="h-100 border"><Card.Body className="py-2"><div className="small text-muted">{label}</div><strong>{summary[status] ?? (status === "pending" ? summary.queued : undefined) ?? getDeliveryCount(currentCampaign, status)}</strong></Card.Body></Card></Col>)}
        </Row>
        {currentCampaign?.purpose === "rsvp" && <>
          <div className="small fw-semibold mb-2">Respostas RSVP desta campanha</div>
          <Row className="g-2 mb-3">
            {[["yes", "Com presença"], ["no", "Sem presença"], ["unanswered", "Sem resposta vinculada"]].map(([key, label]) => <Col key={key} xs={4}><Card className="h-100 border"><Card.Body className="py-2"><div className="small text-muted">{label}</div><strong>{getWhatsAppRsvpCount(rsvpSummary, key) ?? "—"}</strong></Card.Body></Card></Col>)}
          </Row>
          {getWhatsAppRsvpCount(rsvpSummary, "other") > 0 && <div className="small text-muted mb-2">Outros valores de resposta: {getWhatsAppRsvpCount(rsvpSummary, "other")}</div>}
          <div className="small text-muted mb-3">“Sem resposta vinculada” inclui convites sem resposta associada, mesmo quando a mensagem ainda não foi enviada ou falhou.</div>
        </>}
        {resendError && <Alert variant="danger" className="p-2">{resendError}</Alert>}
        <Row className="g-2 mb-2"><Col md={6}><Form.Control value={search} onChange={(event) => { setPage(1); setSearch(event.target.value); }} placeholder="Buscar nome, telefone mascarado ou erro" /></Col><Col md={3}><Form.Select value={statusFilter} onChange={(event) => { setPage(1); setStatusFilter(event.target.value); }}><option value="">Todos os estados</option>{Object.entries(DELIVERY_STATUS).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}</Form.Select></Col><Col md={3}><Button className="w-100" variant="outline-danger" disabled={resendBusy} onClick={() => previewResend(true)}>Prévia: reenviar falhas</Button></Col></Row>
        {deliveries.length > 0 ? <Table responsive hover size="sm" className="mb-0"><thead><tr><th></th><th>Entrega / participante</th><th>Telefone</th><th>Estado</th><th>Tentativas</th><th>Próxima tentativa</th><th>Erro / ação</th></tr></thead><tbody>{deliveries.map((delivery) => <tr key={delivery.delivery_id}><td><Form.Check aria-label={`Selecionar entrega ${delivery.delivery_id}`} disabled={delivery.status !== "failed"} checked={delivery.status === "failed" && resendIds.includes(Number(delivery.delivery_id))} onChange={(event) => { setResendPreview(null); setResendIds((current) => event.target.checked ? [...new Set([...current, Number(delivery.delivery_id)])] : current.filter((id) => id !== Number(delivery.delivery_id))); }} /></td><td><Button variant="link" size="sm" className="p-0 text-start" onClick={() => setDeliveryToInspect(delivery)}>{delivery.name || `Participante #${delivery.user_id || "—"}`}<div className="small text-muted">Entrega #{delivery.delivery_id}</div></Button></td><td>{delivery.masked_phone || "—"}</td><td><StatusBadge status={delivery.status} config={DELIVERY_STATUS} /></td><td>{delivery.attempt_count ?? "—"}</td><td>{toDisplayDate(delivery.next_attempt_at, eventTimezone)}</td><td>{delivery.error_code || "—"}{delivery.error_detail && <div className="small text-muted">{delivery.error_detail}</div>}{delivery.status === "unknown" && <Button size="sm" variant="outline-warning" onClick={() => setDeliveryToInspect(delivery)}>Resolver resultado</Button>}</td></tr>)}</tbody></Table> : <div className="small text-muted">Nenhuma entrega nesta página.</div>}
        <div className="d-flex justify-content-between align-items-center mt-2"><Button size="sm" variant="outline-primary" disabled={!resendIds.length || resendBusy} onClick={() => previewResend(false)}>Prévia: reenviar {resendIds.length} selecionada(s)</Button><div className="d-flex align-items-center gap-2"><Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Anterior</Button><span className="small">{total} entregas · página {page} de {Math.max(1, Math.ceil(total / pageSize))}</span><Button size="sm" variant="outline-secondary" disabled={page * pageSize >= total} onClick={() => setPage((value) => value + 1)}>Próxima</Button></div></div>
        {resendPreview && <Alert variant="warning" className="mt-3 mb-0"><div className="fw-semibold">Prévia de reenvio · {resendPreview.eligible_count ?? resendPreview.delivery_ids?.length ?? 0} destinatário(s)</div><div>{resendPreview.excluded_count ?? 0} suprimido(s) ou em cooldown serão excluídos.</div>{(resendPreview.samples || []).slice(0, 3).map((sample) => <div key={sample.user_id} className="small border-top mt-2 pt-2"><strong>{sample.name} · {sample.masked_phone}</strong><div>{sample.body}</div></div>)}<Button className="mt-2" size="sm" variant="danger" disabled={resendBusy || !resendPreview.can_launch} onClick={executeResend}>Confirmar reenvio vinculado à campanha #{campaign.campaign_id}</Button></Alert>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fechar</Button></Modal.Footer>
      {deliveryToInspect && <WhatsAppDeliveryDetails key={`${eventId}:${deliveryToInspect.delivery_id}`} show delivery={deliveryToInspect} eventId={eventId} eventTimezone={eventTimezone} onHide={() => setDeliveryToInspect(null)} onResolved={async () => { const response = await getWhatsAppCampaign({ campaignId: campaign.campaign_id, eventId, page, pageSize, search, status: statusFilter || undefined }); setDetail(response.data || {}); setDeliveryToInspect(null); }} />}
    </Modal>
  );
};

const CampaignActions = ({ campaign, compact = false, onDetails, onAction }) => (
  <div className={compact ? "d-flex justify-content-end gap-1" : "d-flex flex-wrap gap-2"}>
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={() => onDetails(campaign)}
      aria-label={`Ver detalhes da campanha #${campaign.campaign_id}`}
      title="Ver detalhes"
    >
      <FontAwesomeIcon icon={faCircleInfo} className={compact ? "" : "me-1"} />
      {!compact && "Detalhes"}
    </Button>
    {["scheduled", "running"].includes(campaign.status) && (
      <Button
        size="sm"
        variant="outline-warning"
        onClick={() => onAction(campaign, "pause")}
        aria-label={`Pausar campanha #${campaign.campaign_id}`}
        title="Pausar campanha"
      >
        <FontAwesomeIcon icon={faPause} className={compact ? "" : "me-1"} />
        {!compact && "Pausar"}
      </Button>
    )}
    {campaign.status === "paused" && (
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() => onAction(campaign, "resume")}
        aria-label={`Retomar campanha #${campaign.campaign_id}`}
        title="Retomar campanha"
      >
        <FontAwesomeIcon icon={faRotate} className={compact ? "" : "me-1"} />
        {!compact && "Retomar"}
      </Button>
    )}
    {["draft", "scheduled", "running", "paused"].includes(campaign.status) && (
      <Button
        size="sm"
        variant="outline-danger"
        onClick={() => onAction(campaign, "cancel")}
        aria-label={`Cancelar campanha #${campaign.campaign_id}`}
        title="Cancelar campanha"
      >
        <FontAwesomeIcon icon={faStop} className={compact ? "" : "me-1"} />
        {!compact && "Cancelar"}
      </Button>
    )}
  </div>
);

const CampaignCancellationModal = ({ campaign, eventTimezone, onCancel, onHide }) => (
  <Modal show={Boolean(campaign)} onHide={onHide} centered>
    <Modal.Header closeButton>
      <Modal.Title>Cancelar campanha?</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="mb-2">A campanha <strong>{campaign?.name || `#${campaign?.campaign_id}`}</strong> deixará de reservar novos destinatários. Envios já iniciados podem continuar até receberem confirmação do provedor.</p>
      <div className="border rounded bg-light p-3 small">
        <div><span className="text-muted">Agendamento:</span> {toDisplayDate(campaign?.scheduled_for, eventTimezone)}</div>
        <div><span className="text-muted">Público elegível:</span> {campaign?.eligible_count ?? "a confirmar"}</div>
      </div>
      <p className="small text-muted mb-0 mt-3">O histórico será preservado e chamadas já aceitas pela Meta não podem ser desfeitas.</p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onHide}>Voltar</Button>
      <Button variant="danger" onClick={onCancel}><FontAwesomeIcon icon={faStop} className="me-1" />Cancelar campanha</Button>
    </Modal.Footer>
  </Modal>
);

const WhatsAppChannel = ({ dataTable, updateDataTable }) => {
  const notyf = useContext(NotyfContext);
  const navigate = useNavigate();
  const { setLoading } = useLayout();
  const eventId = dataTable.evento_id;
  const eventTimezone = dataTable?.timezone || PANEL_EVENT_TIME_ZONE;
  const [registrationDelivery, setRegistrationDelivery] = useState(null);
  const [registrationRefresh, setRegistrationRefresh] = useState(0);
  const [senders, setSenders] = useState([]);
  const [settings, setSettings] = useState(defaultSettings);
  const savedConfiguration = useRef({ settings: defaultSettings, ready: false });
  const [rsvpRegistry, setRsvpRegistry] = useState({ definitions: [] });
  const [senderSelectionWarning, setSenderSelectionWarning] = useState(false);
  const [fields, setFields] = useState({ phone: [], rsvp: [], template_variables: [] });
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [configurationReady, setConfigurationReady] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [individualSend, setIndividualSend] = useState(false);
  const [campaignDetails, setCampaignDetails] = useState({ show: false, campaign: null });
  const [campaignToCancel, setCampaignToCancel] = useState(null);
  const [campaignToPause, setCampaignToPause] = useState(null);
  const [pauseReason, setPauseReason] = useState("");
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState("");
  const campaignRequest = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState({});
  const pageLoadKey = JSON.stringify([eventId, updateDataTable]);
  const pageLoadCallbacks = useMemo(() => Object.fromEntries(
    ["settings", "campaigns", "messages", "blocklist", "rsvp"].map((section) => [section, (pending) => {
      setSectionLoading((current) => current[section]?.key === pageLoadKey && current[section]?.pending === pending
        ? current : { ...current, [section]: { key: pageLoadKey, pending } });
    }]),
  ), [pageLoadKey]);
  const pageLoading = ["settings", "campaigns", "messages", "blocklist", "rsvp"]
    .some((section) => sectionLoading[section]?.key !== pageLoadKey || sectionLoading[section]?.pending);

  useEffect(() => {
    if (initialLoading) {
      if (!pageLoading) setInitialLoading(false);
      return;
    }
    setLoading(pageLoading);
  }, [initialLoading, pageLoading, sectionLoading, setLoading]);
  useEffect(() => () => setLoading(false), [setLoading]);

  useEffect(() => {
    const controller = new AbortController();
    pageLoadCallbacks.rsvp(true);
    setRsvpRegistry({ definitions: [] });
    getEventRsvpSettings({ eventId, signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setRsvpRegistry(response.data?.registry || { definitions: [] });
      })
      .catch((error) => {
        if (!controller.signal.aborted) notificationError(notyf, error, "Não foi possível carregar os RSVPs para os envios.");
      })
      .finally(() => {
        if (!controller.signal.aborted) pageLoadCallbacks.rsvp(false);
      });
    return () => controller.abort();
  }, [eventId, notyf, pageLoadCallbacks]);

  const load = useCallback(async ({ signal } = {}) => {
    pageLoadCallbacks.settings(true);
    setLoadError(false);
    try {
      const settingsResponse = await getWhatsAppSettings({ eventId, signal });
      const availableSenders = Array.isArray(settingsResponse.data.senders)
        ? settingsResponse.data.senders
        : (await getWhatsAppSenders({ eventId, signal })).data.senders || [];
      setSenders(availableSenders);
      const remoteSettings = settingsResponse.data.settings || {};
      const hasConfiguredSender = availableSenders.some((sender) => String(sender.sender_id) === String(remoteSettings.sender_id));
      const savedSenderIsUnavailable = Boolean(remoteSettings.sender_id) && !hasConfiguredSender;
      setSenderSelectionWarning(savedSenderIsUnavailable);
      const nextSettings = {
        ...defaultSettings,
        ...remoteSettings,
        sender_id: hasConfiguredSender
          ? String(remoteSettings.sender_id)
          : "",
      };
      setSettings(nextSettings);
      setFields(settingsResponse.data.fields || { phone: [], rsvp: [], template_variables: [] });
      const ready = settingsResponse.data.configuration_ready === true
        && hasConfiguredSender
        && Boolean(remoteSettings.phone_field_input_id);
      setConfigurationReady(ready);
      savedConfiguration.current = { settings: nextSettings, ready };
      if (ready) {
        const templatesResponse = await getWhatsAppTemplates({ eventId, signal });
        setTemplates(templatesResponse.data.templates || []);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      if (!isWhatsAppRequestCancelled(error)) {
        setConfigurationReady(false);
        setTemplates([]);
        setLoadError(true);
        notificationError(notyf, error, "Não foi possível carregar as configurações de WhatsApp.");
      }
    } finally {
      if (!signal?.aborted) pageLoadCallbacks.settings(false);
    }
  }, [eventId, notyf, pageLoadCallbacks]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load, updateDataTable]);

  const saveSettings = async () => {
    if (!settings.sender_id || !settings.phone_field_input_id) {
      notyf.open({ type: "warning", message: "Selecione o remetente e o campo de celular antes de salvar.", ripple: true, dismissible: true });
      return;
    }

    setLoading(true);
    try {
      const { sender_id, phone_field_input_id } = settings;
      await putWhatsAppSettings({
        event_id: eventId,
        sender_id,
        phone_field_input_id,
      });
      notyf.open({ type: "success", message: "Configuração de WhatsApp salva.", ripple: true, dismissible: true });
      await load();
      setRegistrationRefresh((previous) => previous + 1);
    } catch (error) {
      notificationError(notyf, error, "Não foi possível salvar a configuração de WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (field, value) => {
    if (["sender_id", "phone_field_input_id"].includes(field)) {
      setConfigurationReady(false);
    }
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const campaignAction = async (campaignId, action) => {
    setLoading(true);
    try {
      if (action === "pause") {
        await pauseWhatsAppCampaign({ campaignId, eventId, reason: pauseReason.trim() });
      } else if (action === "resume") {
        await resumeWhatsAppCampaign({ campaignId, eventId });
      } else if (action === "cancel") {
        await cancelWhatsAppCampaign({ campaignId, eventId });
      } else {
        throw new Error("WHATSAPP_CAMPAIGN_ACTION_INVALID");
      }
      notyf.open({ type: "success", message: "Campanha atualizada.", ripple: true, dismissible: true });
      await refreshCampaigns();
      setRegistrationRefresh((previous) => previous + 1);
    } catch (error) {
      notificationError(notyf, error, "Não foi possível atualizar a campanha.");
    } finally {
      setLoading(false);
    }
  };

  const requestCampaignAction = (campaign, action) => {
    if (action === "cancel") {
      setCampaignToCancel(campaign);
      return;
    }
    if (action === "pause") {
      setCampaignToPause(campaign);
      setPauseReason("");
      return;
    }
    campaignAction(campaign.campaign_id, action);
  };

  const refreshCampaigns = useCallback(async () => {
    campaignRequest.current?.abort();
    const controller = new AbortController();
    campaignRequest.current = controller;
    pageLoadCallbacks.campaigns(true);
    setCampaignsLoading(true); setCampaignsError("");
    try {
      const rows = await loadWhatsAppCampaignReport(eventId, controller.signal);
      if (!controller.signal.aborted) setCampaigns(rows);
    } catch (error) {
      if (!controller.signal.aborted) setCampaignsError("Não foi possível carregar as campanhas. Atualize para tentar novamente.");
    } finally { if (!controller.signal.aborted) { setCampaignsLoading(false); pageLoadCallbacks.campaigns(false); } }
  }, [eventId, pageLoadCallbacks]);

  useEffect(() => {
    refreshCampaigns();
    return () => campaignRequest.current?.abort();
  }, [refreshCampaigns, updateDataTable]);

  const activeSender = senders.find((sender) => String(sender.sender_id) === String(settings.sender_id));
  const canCreateCampaign = activeSender?.status === "active";
  const canSaveEventConfiguration = Boolean(settings.sender_id && settings.phone_field_input_id);
  const configurationContent = (
    <div className="border rounded p-3 mb-3">
      <h6>Configuração do envio</h6>
          <p className="text-muted small mb-3">
            Escolha o remetente e o campo de telefone antes de continuar. Estas definições ficam salvas no evento e também são utilizadas pelos envios automáticos após cadastro.
          </p>
          <Row>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Remetente utilizado</Form.Label>
              <Form.Select
                value={settings.sender_id || ""}
                disabled={senders.length === 0}
                onChange={(event) => {
                  setSenderSelectionWarning(false);
                  updateSettings("sender_id", event.target.value);
                }}
              >
                <option value="">Selecione um remetente</option>
                {senders.map((sender) => (
                  <option key={sender.sender_id} value={sender.sender_id}>
                    {sender.display_name || "WhatsApp"} · {sender.display_phone || "número não informado"}
                    {sender.is_default ? " (número R1)" : ""}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className={senderSelectionWarning ? "text-warning" : "text-muted"}>
                {senderSelectionWarning
                  ? "O remetente salvo não está mais autorizado. Escolha outro e salve novamente."
                  : "A escolha precisa ser salva neste evento; nenhum remetente é ativado automaticamente."}
              </Form.Text>
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Campo de telefone/celular do formulário</Form.Label>
              <Form.Select
                value={settings.phone_field_input_id || ""}
                disabled={senders.length === 0}
                onChange={(event) => updateSettings("phone_field_input_id", event.target.value)}
              >
                <option value="">Selecione o campo</option>
                {(fields.phone || []).map((field) => (
                  <option key={field.inputID} value={field.inputID}>
                    {field.label || `Campo ${field.inputID}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          {senders.length === 0 ? (
            <Button variant="outline-primary" onClick={() => navigate("/configuracoes-do-sistema")}>Configurar remetente primeiro</Button>
          ) : (
            <Button variant="primary" onClick={saveSettings} disabled={!canSaveEventConfiguration}>
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              {configurationReady ? "Salvar alterações" : "Salvar e continuar"}
            </Button>
          )}

    </div>
  );


  return (
    <>
      {initialLoading && <div className="py-5"><Loader /></div>}
      <div className={initialLoading ? "d-none" : undefined} aria-hidden={initialLoading || undefined}>
      {(loadError || senders.length === 0) && (
        <Card className="mb-4">
          <Card.Body>
          {loadError && <Alert variant="danger" className="p-2 mb-3"><FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />A integração de WhatsApp não respondeu. Confirme se a migração e o servidor central foram publicados antes de configurar uma campanha.</Alert>}
          {senders.length === 0 && <Alert variant="primary" className="p-2 mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2"><span><FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />Nenhum remetente está disponível. Configure o número R1 da Cloud API antes do primeiro envio.</span><Button size="sm" variant="outline-primary" onClick={() => navigate("/configuracoes-do-sistema")}>Configurar remetente</Button></Alert>}
          </Card.Body>
        </Card>
      )}


      <WhatsAppRegistrationHistoryCard eventId={eventId} eventTimezone={eventTimezone} eventTitle={dataTable.title}
        campaigns={campaigns} campaignsLoading={campaignsLoading && campaigns.length === 0} campaignsError={campaignsError}
        refreshKey={`${updateDataTable}:${registrationRefresh}`} onInspect={setRegistrationDelivery}
        onLoadState={pageLoadCallbacks.messages} onCreate={() => { setIndividualSend(true); setShowCampaignModal(true); }}
        createDisabled={loadError || senders.length === 0} />
      {registrationDelivery && <WhatsAppDeliveryDetails key={`${eventId}:${registrationDelivery.delivery_id}`}
        show delivery={registrationDelivery} eventId={eventId} eventTimezone={eventTimezone}
        onHide={() => setRegistrationDelivery(null)} onResolved={() => { setRegistrationDelivery(null); setRegistrationRefresh((previous) => previous + 1); }} />}

      <WhatsAppCampaignReport eventId={eventId} eventTitle={dataTable.title} eventTimezone={eventTimezone}
        campaigns={campaigns} loading={campaignsLoading} error={campaignsError}
        onCreate={() => { setIndividualSend(false); setShowCampaignModal(true); }} createDisabled={loadError || senders.length === 0}
        renderActions={(campaign) => <CampaignActions compact campaign={campaign}
          onDetails={(selected) => setCampaignDetails({ show: true, campaign: selected })} onAction={requestCampaignAction} />} />
      <WhatsAppBlocklist eventId={eventId} eventTitle={dataTable.title} eventTimezone={eventTimezone}
        refreshKey={updateDataTable + ":" + registrationRefresh} onLoadState={pageLoadCallbacks.blocklist} />

      <CampaignModal show={showCampaignModal} onHide={() => { setShowCampaignModal(false); setSettings(savedConfiguration.current.settings); setConfigurationReady(savedConfiguration.current.ready); }} eventId={eventId} eventTimezone={eventTimezone} settings={settings} senders={senders} templates={templates} individual={individualSend}
        registrationEndDate={dataTable.json?.form?.register_end_date} onRsvpRegistryChange={setRsvpRegistry}
        configurationReady={configurationReady && canCreateCampaign} configurationContent={configurationContent} templateVariableFields={fields.template_variables || []} rsvpRegistry={rsvpRegistry} onCreated={async () => { await refreshCampaigns(); setRegistrationRefresh((previous) => previous + 1); }} />
      {campaignDetails.show && campaignDetails.campaign && <CampaignDetailsModal key={`${eventId}:${campaignDetails.campaign.campaign_id}`} show onHide={() => setCampaignDetails({ show: false, campaign: null })} eventId={eventId} eventTimezone={eventTimezone} campaign={campaignDetails.campaign} />}
      <CampaignCancellationModal
        campaign={campaignToCancel}
        eventTimezone={eventTimezone}
        onHide={() => setCampaignToCancel(null)}
        onCancel={() => {
          if (!campaignToCancel) return;
          const campaignId = campaignToCancel.campaign_id;
          setCampaignToCancel(null);
          campaignAction(campaignId, "cancel");
        }}
      />
      <Modal show={Boolean(campaignToPause)} onHide={() => setCampaignToPause(null)} centered>
        <Modal.Header closeButton><Modal.Title>Pausar campanha #{campaignToPause?.campaign_id}</Modal.Title></Modal.Header>
        <Modal.Body><p>Informe o motivo operacional. Envios pendentes serão mantidos e novos envios ficam suspensos.</p><Form.Control as="textarea" rows={3} value={pauseReason} onChange={(event) => setPauseReason(event.target.value)} placeholder="Motivo da pausa" /></Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={() => setCampaignToPause(null)}>Voltar</Button><Button variant="warning" disabled={!pauseReason.trim()} onClick={() => { const id = campaignToPause.campaign_id; setCampaignToPause(null); campaignAction(id, "pause"); }}>Pausar campanha</Button></Modal.Footer>
      </Modal>
      </div>
    </>
  );
};

export const EventCommunicationChannels = ({ dataTable, updateDataTable }) => {
  const dispatch = useAppDispatch();
  const userState = useAuth();
  const canViewWhatsApp = canAccessEventWhatsApp(userState, dataTable.platform);

  useEffect(() => {
    if (!canViewWhatsApp) {
      dispatch(eventList({}));
    }
  }, [canViewWhatsApp, dispatch]);

  if (!canViewWhatsApp) return null;

  return (
    <Container fluid className="p-0">
      <WhatsAppChannel key={dataTable.evento_id} dataTable={dataTable} updateDataTable={updateDataTable} />
    </Container>
  );
};

export const EventWhatsApp = EventCommunicationChannels;
